/**
 * Host resolution.
 *
 * One SPA serves two very different surfaces, distinguished only by hostname:
 *
 *   oyoai.localtest.me          the apex: landing, sign-in, create space
 *   acme.oyoai.localtest.me     a space dashboard
 *
 * This is how the product works in production too (<space>.oyoai.space), so the
 * apex/space split is a routing concern, not a development hack.
 */

export const baseDomain: string = import.meta.env.VITE_BASE_DOMAIN || 'oyoai.localtest.me'

const host = window.location.hostname

/** Port suffix for cross-origin links, so dev-on-a-port still works. */
const portSuffix = window.location.port ? `:${window.location.port}` : ''

/**
 * The apex serves the marketing/entry surface. `localhost` and `127.0.0.1` are
 * treated as apex hosts so the app degrades usefully when hit directly rather
 * than through Caddy.
 */
export const isApexHost: boolean =
  host === baseDomain ||
  host === `www.${baseDomain}` ||
  host === 'localhost' ||
  host === '127.0.0.1'

/**
 * The space slug, taken from the hostname. Only the first label counts, so a
 * nested name such as `a.b.oyoai.space` resolves to `a`.
 *
 * null on the apex, which is what every space-scoped guard keys off.
 */
export const spaceSlug: string | null = (() => {
  if (isApexHost) return null
  if (!host.endsWith(`.${baseDomain}`)) return null
  const label = host.slice(0, -(baseDomain.length + 1))
  if (!label || label.includes('.')) {
    // A nested subdomain is not a space; take the leftmost label anyway so a
    // misconfigured name fails as "space not found" rather than as a blank app.
    return label.split('.')[0] || null
  }
  return label
})()

function originFor(hostname: string): string {
  return `${window.location.protocol}//${hostname}${portSuffix}`
}

/** Absolute URL on the apex. Space switching is a cross-origin navigation. */
export function apexUrl(path = '/'): string {
  return `${originFor(baseDomain)}${path}`
}

/** Absolute URL for a space dashboard. */
export function spaceUrl(slug: string, path = '/'): string {
  return `${originFor(`${slug}.${baseDomain}`)}${path}`
}

/** This space's own dashboard origin. */
export function currentSpaceUrl(path = '/'): string {
  return `${window.location.origin}${path}`
}
